import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import MappingAddForm from '../components/snoop/MappingAddForm'
import GeniusArtistSearch from '../components/snoop/GeniusArtistSearch'
import {
  createPubMapping,
  deletePubMapping,
  fetchPubMappings,
  updatePubMapping,
  updatePubMappingAction,
  updatePubMappingLeadAr,
  updatePubMappingStatus,
  uploadPgpStatusBatch,
} from '../api/pgpClient'
import {
  PGP_LEAD_AR_OPTIONS,
  PGP_STATUS_OPTIONS,
  comparePgpMappingStatus,
} from '../utils/snoop/statusOptions'
import { PLACEHOLDER_PGP_MAPPING } from '../utils/snoop/placeholderColumns'

const btnClass =
  'inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

function requireIdentity(mappingId, uid, label) {
  const hasId = mappingId != null && String(mappingId).trim() !== ''
  const hasUid = uid != null && String(uid).trim() !== ''
  if (!hasId && !hasUid) {
    throw new Error(`Missing Pub Mapping ID / Genius Artist ID for ${label}`)
  }
}

export default function PgpMappingPage() {
  const [manualOnly, setManualOnly] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  // Bumped after an add so the table refetches.
  const [refreshKey, setRefreshKey] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState(null)
  // Prefill from a Genius search pick. MappingAddForm seeds its state once, so
  // `addSeed.key` remounts it to pick the new initial values up.
  const [addSeed, setAddSeed] = useState({ key: 0, values: {} })

  const fetchData = useCallback(
    () => fetchPubMappings({ manualOnly }),
    // refreshKey is intentionally a dependency: it forces a refetch after adds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manualOnly, refreshKey],
  )

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'pub_mappings',
        uidColumn: 'Genius Artist ID',
        rowIdColumn: 'Pub Mapping ID',
        options: PGP_STATUS_OPTIONS,
        centerText: true,
        allowBlank: true,
        timestampColumn: 'Updated At',
        onSave: async ({ status, row, mappingId, uid }) => {
          requireIdentity(mappingId, uid, 'status update')
          await updatePubMappingStatus({
            id: mappingId,
            geniusArtistId: uid,
            status,
            name: row?.Name,
            geniusUrl: row?.['Genius URL'],
          })
        },
      },
      'Lead A&R': {
        target: 'pub_mappings',
        uidColumn: 'Genius Artist ID',
        rowIdColumn: 'Pub Mapping ID',
        options: PGP_LEAD_AR_OPTIONS,
        centerText: true,
        allowBlank: true,
        timestampColumn: 'Updated At',
        onSave: async ({ status, mappingId, uid }) => {
          requireIdentity(mappingId, uid, 'Lead A&R update')
          await updatePubMappingLeadAr({
            id: mappingId,
            geniusArtistId: uid,
            leadAr: status,
          })
        },
      },
      Action: {
        target: 'pub_mappings',
        uidColumn: 'Genius Artist ID',
        rowIdColumn: 'Pub Mapping ID',
        // "Ball in our court" is a boolean — a checkbox, not a dropdown.
        control: 'checkbox',
        checkboxTitle: 'Ball in our court',
        timestampColumn: 'Updated At',
        optimisticPatch: (next) => ({ Action: next === 'Yes' }),
        onSave: async ({ status, mappingId, uid }) => {
          requireIdentity(mappingId, uid, 'action update')
          await updatePubMappingAction({
            id: mappingId,
            geniusArtistId: uid,
            action: status === 'Yes',
          })
        },
      },
    }),
    [],
  )

  /**
   * Pencil edit for the fields that are not dropdowns. Every field sends its
   * `update*` flag so a cleared box actually clears that column, while columns
   * the modal does not own are left untouched.
   */
  const editablePubMapping = useMemo(
    () => ({
      onSave: async ({
        mappingId,
        name,
        geniusArtistId,
        clearGeniusArtistId,
        lawyer,
        calc,
        committedMoney,
        uncommitted,
        optionMoney,
        notes,
      }) => {
        if (mappingId == null || String(mappingId).trim() === '') {
          throw new Error('Missing Pub Mapping ID')
        }
        const clearId = Boolean(clearGeniusArtistId) || !geniusArtistId
        await updatePubMapping(mappingId, {
          name: name || null,
          geniusArtistId: clearId ? null : geniusArtistId,
          setGeniusArtistId: !clearId,
          clearGeniusArtistId: clearId,
          lawyer: lawyer || null,
          updateLawyer: true,
          calc: calc || null,
          updateCalc: true,
          committedMoney: committedMoney ?? null,
          updateCommittedMoney: true,
          uncommitted: uncommitted ?? null,
          updateUncommitted: true,
          optionMoney: optionMoney ?? null,
          updateOptionMoney: true,
          notes: notes || null,
          updateNotes: true,
        })
      },
    }),
    [],
  )

  async function handleUpload(event) {
    const file = event.target.files?.[0]
    // Reset so re-picking the same file fires change again.
    event.target.value = ''
    if (!file) return
    setUploading(true)
    setUploadMessage(null)
    try {
      const result = await uploadPgpStatusBatch(file)
      const bits = [
        result?.updated ? `${result.updated} updated` : null,
        result?.created ? `${result.created} created` : null,
        result?.skipped ? `${result.skipped} skipped` : null,
      ].filter(Boolean)
      setUploadMessage({
        ok: true,
        text: bits.length ? `Applied: ${bits.join(', ')}` : 'Nothing to apply',
      })
      setRefreshKey((k) => k + 1)
    } catch (err) {
      setUploadMessage({ ok: false, text: err?.message || String(err) })
    } finally {
      setUploading(false)
    }
  }

  const onDeleteRow = useCallback(async (row) => {
    const mappingId = row?.['Pub Mapping ID']
    if (mappingId == null || String(mappingId).trim() === '') {
      throw new Error('Missing Pub Mapping ID')
    }
    await deletePubMapping(mappingId)
  }, [])

  /**
   * Two-level: Status in pipeline order (Longform → Proposal → Priority →
   * Watching → Archive → the rest), then most-recently-touched first within a
   * status. `asc` walks the rank ascending, which is that stage order.
   */
  const defaultSort = useMemo(
    () => [
      { column: 'Status', direction: 'asc' },
      { column: 'Updated At', direction: 'desc' },
    ],
    [],
  )

  const uploadBanner = uploadMessage ? (
    <div
      className={[
        'rounded-xl border px-4 py-3 text-sm',
        uploadMessage.ok
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
          : 'border-rose-200 bg-rose-50 text-rose-800',
      ].join(' ')}
    >
      {uploadMessage.text}
    </div>
  ) : null

  const addForm = showAdd ? (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <GeniusArtistSearch
        onPick={(hit) =>
          setAddSeed((prev) => ({
            key: prev.key + 1,
            values: {
              ...prev.values,
              name: hit?.name || '',
              geniusArtistId: hit?.id != null ? String(hit.id) : '',
              geniusUrl: hit?.url || '',
            },
          }))
        }
      />
      <MappingAddForm
        key={addSeed.key}
        title="Add writer / producer"
        submitLabel="Add"
        initialValues={addSeed.values}
        fields={[
          { name: 'name', label: 'Name', required: true },
          { name: 'geniusArtistId', label: 'Genius Artist ID', placeholder: 'optional' },
          { name: 'geniusUrl', label: 'Genius URL', placeholder: 'optional' },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: [{ value: '', label: '—' }, ...PGP_STATUS_OPTIONS],
            defaultValue: 'Watching',
          },
          {
            name: 'leadAr',
            label: 'Lead A&R',
            type: 'select',
            options: [{ value: '', label: '—' }, ...PGP_LEAD_AR_OPTIONS],
          },
          { name: 'lawyer', label: 'Lawyer' },
          { name: 'calc', label: 'Calc' },
          { name: 'committedMoney', label: 'Committed $' },
          { name: 'uncommitted', label: 'Uncommitted' },
          { name: 'optionMoney', label: 'Option' },
          { name: 'notes', label: 'Notes' },
          { name: 'action', label: 'Ball in our court', type: 'checkbox' },
        ]}
        onSubmit={async (values) => {
          await createPubMapping(values)
          setAddSeed({ key: 0, values: {} })
          setRefreshKey((k) => k + 1)
        }}
      />
    </div>
  ) : null

  const extraActions = (
    <>
      <label
        className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm"
        title="Only rows with no Genius Artist ID"
      >
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
          checked={manualOnly}
          onChange={(e) => setManualOnly(e.target.checked)}
        />
        Manual only
      </label>
      <button
        type="button"
        className={btnClass}
        onClick={() => setShowAdd((on) => !on)}
        aria-pressed={showAdd}
      >
        {showAdd ? 'Close add' : 'Add artist'}
      </button>
      <label
        className={btnClass}
        title="Excel/CSV with Status + Pub Mapping ID or Genius Artist ID (the Charts 'By artist' export works as-is)"
      >
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          disabled={uploading}
          onChange={handleUpload}
        />
        {uploading ? 'Uploading…' : 'Upload statuses'}
      </label>
    </>
  )

  return (
    <SummaryDashboard
      title="Mapping"
      subtitle="Full pub_mappings registry — status, Lead A&R, and deal fields"
      fetchData={fetchData}
      defaultSort={defaultSort}
      statusCompare={comparePgpMappingStatus}
      searchPlaceholder="Search name, Genius id, lawyer, calc, notes…"
      editableStatus={editableStatus}
      editablePubMapping={editablePubMapping}
      onDeleteRow={onDeleteRow}
      extraActions={extraActions}
      addForm={
        uploadBanner || addForm ? (
          <div className="space-y-3">
            {uploadBanner}
            {addForm}
          </div>
        ) : null
      }
      layout="mapping"
      headerLabels={{ 'Lead A&R': 'Lead', Name: 'Artist' }}
      truncateColumns={['Name', 'Lawyer', 'Notes']}
      hiddenColumns={['Genius URL', 'Created At', 'Pub Mapping ID']}
      placeholderColumns={PLACEHOLDER_PGP_MAPPING}
      tableKey={`pgp-mapping-${manualOnly ? 'manual' : 'all'}-${refreshKey}`}
    />
  )
}
